// filepath: apps/web/src/runtime/focusTrap.ts
//
// FR-UI-CORE-9 — WCAG 2.1.2 / 2.4.3 focus trap utility.
//
// Used by modal dialogs (`ConflictResolution`, `DeprovisionDialog`)
// to contain keyboard focus within the dialog. The helper:
//   - traps Tab / Shift+Tab within the container;
//   - moves focus to the first focusable element on activation;
//   - dispatches a `cancel` CustomEvent on Escape (caller's listener
//     closes the dialog — keeps the utility free of side-effect
//     knowledge about who mounted it);
//   - restores focus to the previously-focused element on teardown.
//
// Contract (WCAG 2.4.3 Focus Order + 2.1.2 No Keyboard Trap):
//   - Focus cannot escape the container via Tab.
//   - The trap is opt-in: callers invoke `focusTrap(containerEl)`
//     and store the returned teardown function.
//   - The teardown function is idempotent (safe to call twice).
//
// SECURITY: no tenant data is touched. The helper operates on
// DOM elements only.

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function getFocusable(container: HTMLElement): HTMLElement[] {
  const nodes = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  );
  return nodes.filter((n) => !n.hasAttribute("disabled") && n.tabIndex !== -1);
}

function dispatchCancel(container: HTMLElement): void {
  // CustomEvent — caller listens for `cancel` and dismisses. The
  // utility intentionally does not import any modal-specific
  // dependencies so it stays generic.
  container.dispatchEvent(
    new CustomEvent("cancel", { bubbles: false, cancelable: true }),
  );
}

export interface FocusTrapOptions {
  /**
   * Element to focus on activation. Defaults to the first focusable
   * child of `container`. Set explicitly when the dialog has a
   * safer default (e.g. the Cancel button on a destructive
   * confirmation dialog).
   */
  initialFocus?: HTMLElement | null;
  /**
   * If `true`, pressing Escape dispatches a `cancel` event on the
   * container. Defaults to `true` per the WCAG pattern of modal
   * dialogs being dismissible via Escape.
   */
  enableEscape?: boolean;
}

/**
 * Activate a focus trap on `container`. Returns a teardown function
 * that releases the trap and restores focus to the element that
 * was focused before activation.
 */
export function focusTrap(
  container: HTMLElement,
  options: FocusTrapOptions = {},
): () => void {
  const { initialFocus, enableEscape = true } = options;

  // Remember who had focus so we can restore it on teardown. This
  // is what makes the trap non-disruptive — the user's place in
  // the document is preserved when the dialog closes.
  const previouslyFocused = document.activeElement as HTMLElement | null;

  // Focus the initial element on next tick — the container may not
  // be in the DOM yet at the call site (e.g. portal mount).
  const focusable = getFocusable(container);
  const firstFocusable = focusable[0] ?? container;
  const target = initialFocus ?? firstFocusable;
  // tabIndex must be set on the container itself so the fallback
  // path (no focusable children) is focusable.
  if (target === container && !container.hasAttribute("tabindex")) {
    container.tabIndex = -1;
  }
  // Only auto-focus if the user is not already inside the dialog
  // (or in the container itself). This is the "polite" trap: it
  // contains focus without stealing it from a user who is already
  // driving the dialog. test environments and screen-reader users
  // both benefit — the former avoids racing with userEvent; the
  // latter avoids re-announcing a re-focus that was already heard.
  //
  // The re-check happens at rAF time (not at mount time) so a
  // userEvent-driven focus event that races the trap is honored.
  // The test suite uses userEvent.type / .click which moves focus
  // into the container synchronously; the rAF re-check then
  // notices and skips the auto-focus.
  requestAnimationFrame(() => {
    const active = document.activeElement as HTMLElement | null;
    const alreadyInContainer =
      active !== null && (active === container || container.contains(active));
    if (alreadyInContainer) return;
    try {
      target.focus();
    } catch {
      // focus() can throw on detached elements in some test
      // environments. Swallow: the trap is still active; the
      // initial focus will resolve on the next Tab press.
    }
  });

  function onKeyDown(e: KeyboardEvent): void {
    if (e.key === "Escape" && enableEscape) {
      e.stopPropagation();
      dispatchCancel(container);
      return;
    }
    if (e.key !== "Tab") return;
    const items = getFocusable(container);
    if (items.length === 0) {
      // No focusable elements — prevent Tab from escaping.
      e.preventDefault();
      return;
    }
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey) {
      if (active === first || !container.contains(active)) {
        e.preventDefault();
        last.focus();
      }
      return;
    }
    if (active === last || !container.contains(active)) {
      e.preventDefault();
      first.focus();
    }
  }

  document.addEventListener("keydown", onKeyDown, true);

  let torn = false;
  return function teardown(): void {
    if (torn) return;
    torn = true;
    document.removeEventListener("keydown", onKeyDown, true);
    if (previouslyFocused && typeof previouslyFocused.focus === "function") {
      try {
        previouslyFocused.focus();
      } catch {
        // Previously-focused element may have been unmounted.
        // Restore failure is non-fatal — the document retains focus.
      }
    }
  };
}
